import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { BdsTableCell, BdsTableRow, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { getMonthTwoDigits, toUTCDateString } from '../../../../utils/date';
import { PageHeaderComponent } from '../../components/Metrics/PageHeader/PageHeaderComponent';
import { MeuConsumptionPaper } from '../styles';
import MeuConsumptionContext from '../../../../contexts/MeuConsumptionContext';
import { origin, REPORT_PERIOD_SELECT } from '../../../../shared/SegmentEvents';
import { billingRoutes } from '../../../../routes/BillingRoutes';
import { BillingMetrics } from '../../../../shared/BillingMetrics';
import FeatureToggleService from '../../../../services/FeatureToggleService';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import { useSortedPaginatedData } from '../../../../hooks/useSortedPaginatedData';
import { DEFAULT_PAGE_OPTIONS, COLUMN_WIDTHS_3 as COLUMN_WIDTHS } from '../../../../utils/paginationUtils';

const COLUMN_KEYS = ['dateReference', 'totalMeu', 'totalMsg'];

const parseMeuConsumption = (obj, locale: string) => ({
  ...obj,
  totalMeu: obj.metricConsumption.find((item) => item.metricId !== BillingMetrics.activeMessage)?.total ?? 0,
  totalMsg: obj.metricConsumption.find((item) => item.metricId === BillingMetrics.activeMessage)?.total ?? 0,
  dateReference: toUTCDateString(obj.dateReference, locale),
});

export interface MeuConsumptionDailyUsageProps {
  isDetailedView?: boolean;
}

const MeuConsumptionDailyUsageComponent: FC<MeuConsumptionDailyUsageProps> = ({ isDetailedView = false }) => {
  const {
    dateToSearch,
    periodOptions,
    content,
    tenant,
    loggedUser,
    setHeaderContent,
    trackingProps,
    getPlanStatus,
    setDateToSearch,
    billingMeuConsumptionApi,
    dailyMeuConsumptionDataState,
    setDailyMeuConsumptionDataState,
    meuMetricIds,
  } = useContext(MeuConsumptionContext);
  const [isLoading, setIsLoading] = useState(false);

  const { itemsPerPage, sortConfig, sortedData, paginatedData, handlePaginationChange, handleItemsPerPageChange, handleSortChange } =
    useSortedPaginatedData({ data: dailyMeuConsumptionDataState });

  useEffect(() => {
    setHeaderContent({
      redirect: billingRoutes.dataConsumption,
      text: content.formatString(content.header, tenant.info.name),
    });
    return () => setHeaderContent({ redirect: null });
  }, []);

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        const storageType =
          (await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.monthlyEngagedUser)) ? 1 : 0;
        const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
        const meuMetricIdsString = meuMetricIds.join(';');
        const rawData = await billingMeuConsumptionApi.getDailyMeuConsumption(
          tenant.info.id,
          meuMetricIdsString,
          monthString,
          -1,
          storageType,
        );
        setDailyMeuConsumptionDataState(rawData.map((obj) => parseMeuConsumption(obj, content.locale)));
      } finally {
        setIsLoading(false);
      }
    };
    getData();
  }, [dateToSearch]);

  const tableRows = useMemo(
    () =>
      paginatedData.length > 0
        ? paginatedData.map((item) => (
            <BdsTableRow key={item.dateReference}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.dateReference}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.totalMeu.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.totalMsg.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedData, content.locale],
  );

  const periodOptionChangeEvent = async (selectedOption) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    const planStatus = await getPlanStatus();
    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      origin: origin.MEUPERCHATBOT,
      date: dateToRetrieveData,
      planStatus,
    });
  };

  return (
    <>
      {isDetailedView && (
        <PageHeaderComponent
          title={content.daily.title}
          dateToSearch={dateToSearch}
          periodOptions={periodOptions}
          periodOptionChangeEvent={periodOptionChangeEvent}
        />
      )}
      <MeuConsumptionPaper>
        {!isDetailedView && (
          <BdsTypo variant="fs-24" bold="bold" margin={false}>
            {content.daily.title}
          </BdsTypo>
        )}
        <BdsTypo variant="fs-14" margin={false}>
          {content.daily.subtitle}
        </BdsTypo>
        <TableWithPagination
          options={{
            heading: [
              content.daily.columnDateReference,
              content.daily.columnTotalMiu,
              content.daily.columnTotalMsg,
            ],
            columnKeys: COLUMN_KEYS,
            columnWidths: COLUMN_WIDTHS,
            sortConfig,
            onSortChange: handleSortChange,
            itemsPerPageList: DEFAULT_PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: isLoading ? itemsPerPage : sortedData.length,
            isLoading,
            hasEmptyState: !isLoading && sortedData.length === 0,
            emptyStateSubtitle: '',
            isAutoHeight: true,
          }}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {tableRows}
        </TableWithPagination>
      </MeuConsumptionPaper>
    </>
  );
};

export default MeuConsumptionDailyUsageComponent;
